import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, RouteObject, useRoutes } from 'react-router-dom';
import Container from '@mui/material/Container';
import AppPageLayout from '../layouts/AppPageLayout';
import { PermissionPropsItem } from '../layouts/AppPageLayout/Sidebar/types';
import PublicPageLayout from '../layouts/PublicPageLayout';
import { ActionList } from '../pages/ActionList';
import { Analytics } from '../pages/Analytics';
import { AssetList } from '../pages/AssetList';
import { BatchExecution } from '../pages/BatchExecution';
import { BatchExecutionList } from '../pages/BatchExecutionList';
import { BenchmarkList } from '../pages/BenchmarkList';
import { CurrencyList } from '../pages/CurrencyList';
import { Dashboard } from '../pages/Dashboard';
import { EarlySignupUserList } from '../pages/EarlySignupUserList';
import { Employee } from '../pages/Employee';
import { EmployeeList } from '../pages/EmployeeList';
import { Error } from '../pages/Error';
import { FeeBatch } from '../pages/FeeBatch';
import { FeeBatchList } from '../pages/FeeBatchList';
import { FeeConfig } from '../pages/FeeConfig';
import { FeeConfigList } from '../pages/FeeConfigList';
import { ForgotPassword } from '../pages/ForgotPassword';
import { FundAssetClassList } from '../pages/FundAssetClassList';
import { FundGeographicalFocusList } from '../pages/FundGeographicalFocusList';
import { FundManagerList } from '../pages/FundManagerList';
import { HoldingCompanyList } from '../pages/HoldingCompanyList';
import { FundValuationDayList } from '../pages/FundValuationDayList';
import { FundValuationFrequencyList } from '../pages/FundValuationFrequencyList';
import { IndexFund } from '../pages/IndexFund';
import { IndexFundList } from '../pages/IndexFundList';
import { IndexFundNav } from '../pages/IndexFundNavList';
import { IndexFundNavHistory } from '../pages/IndexFundNavHistory';
import { Login } from '../pages/Login';
import { OrderExecution } from '../pages/OrderExecution';
import { OrderExecutionList } from '../pages/OrderExecutionList';
import { Payment } from '../pages/Payment';
import { Permissions } from '../pages/Permissions';
import { Portfolio } from '../pages/Portfolio';
import { PortfolioList } from '../pages/PortfolioList';
import { PortfolioOrder } from '../pages/PortfolioOrder';
import { PortfolioOrderList } from '../pages/PortfolioOrderList';
import { ProductTypeList } from '../pages/ProductTypeList';
import { PromoCodesList } from '../pages/PromoCodes';
import { PromoCodeUsages } from '../pages/PromoCodeUsages/PromoCodeUsages';
import { ThriftCompanies } from '../pages/RatibiPlus/Companies/ThriftCompanies';
import { CreateCompany } from '../pages/RatibiPlus/Companies/CreateCompany';
import UploadCompanyDocument from '../pages/RatibiPlus/Companies/UploadCompanyDocument';
import { CompanyWithdrawRequests } from '../pages/RatibiPlus/CompanyWithdraw/CompanyWithdrawRequests';
import { CreateWithdrawRequest } from '../pages/RatibiPlus/CompanyWithdraw/CreateWithdrawRequest';
import { CompanyTreasury } from '../pages/RatibiPlus/Treasury';
import { CompanyGroups } from '../pages/RatibiPlus/CompanyGroups/CompanyGroups';
import { CreateCompanyGroup } from '../pages/RatibiPlus/CompanyGroups/CreateCompanyGroup';
import { CreateCompanyEmployee } from '../pages/RatibiPlus/Employees/CreateCompanyEmployee';
import { OnboardingEmployees } from '../pages/RatibiPlus/Employees/OnboardingEmployees';
import { NotificationTemplateList } from '../pages/NotificationTemplateList';
import { UserGiftList } from '../pages/UserGiftList';
import { AccountDeletionList } from '../pages/AccountDeletionList';
import { BankLookupList } from '../pages/BankLookupList';
import { AlpacaFeeList } from '../pages/AlpacaFeeList';
import { AlpacaFeeReport } from '../pages/AlpacaFeeReport';
import { Report } from '../pages/Report';
import { FeeCalculationsReportPage } from '../pages/Report/FeeCalculationsReportPage';
import { FinanceUsersReportPage } from '../pages/Report/FinanceUsersReportPage';
import { PortfolioCompositionReportPage } from '../pages/Report/PortfolioCompositionReportPage';
import { PortfolioSnapshotReportPage } from '../pages/Report/PortfolioSnapshotReportPage';
import {
  REPORT_SEGMENT_FEE_CALCULATIONS,
  REPORT_SEGMENT_FINANCE_USERS,
  REPORT_SEGMENT_PORTFOLIO_COMPOSITION,
  REPORT_SEGMENT_PORTFOLIO_SNAPSHOT,
  REPORT_SEGMENT_USER_DETAILS,
} from '../pages/Report/reportRouteSegments';
import { UserDetailsReportPage } from '../pages/Report/UserDetailsReportPage';
import { ResourceList } from '../pages/ResourceList';
import { RoleList } from '../pages/RoleList';
import { RolePermissionList } from '../pages/RolePermissionList';
import { Settings } from '../pages/Settings';
import { SubscribedPortfolio } from '../pages/SubscribedPortfolio';
import { SubscribedPortfolioList } from '../pages/SubscribedPortfolioList';
import { User } from '../pages/User';
import { UserCategoryList } from '../pages/UserCategoryList';
import { UserFeeDeduction } from '../pages/UserFeeDeduction';
import { UserFeeDeductionList } from '../pages/UserFeeDeductionList';
import { UserGroupList } from '../pages/UserGroupList';
import { UserList } from '../pages/UserList';
import { UserPortfolioRequest } from '../pages/UserPortfolioRequest';
import { UserPortfolioRequestList } from '../pages/UserPortfolioRequestList';
import { authSelector } from '../redux/features/auth/auth.selectors';
import { useFetchMyPermissionsQuery } from '../redux/features/rolePermission/rolePermission.apiSlice';
import { locations } from './locations';
import * as permissions from '../utils/permissions';

import { CompanyDepartments } from '../pages/RatibiPlus/Departments/CompanyDepartments';
import { CreateCompanyDepartment } from '../pages/RatibiPlus/Departments/CreateCompanyDepartment';
import { CsvTempletes } from '../pages/RatibiPlus/CsvTempletes/CsvTempletes';
import CreateCsvTemplete from '../pages/RatibiPlus/CsvTempletes/CreateCsvTemplete';
import { DocumentTypes } from '../pages/RatibiPlus/DocumentTypes/DocumentTypes';
import { CreateDocumentType } from '../pages/RatibiPlus/DocumentTypes/CreateDocumentType';
import { LedgerB2b, LedgerB2c } from '../pages/Ledger';
import { AnalyticsCron } from '../pages/RatibiPlus/AnalyticsCron/AnalyticsCron';
import { IbanVerification } from '../pages/IbanVerification/IbanVerification';

interface PermissionRouteObject extends Omit<RouteObject, 'children'> {
  permission?: PermissionPropsItem;
  children?: PermissionRouteObject[];
}

const routesByPermissions: PermissionRouteObject[] = [
  {
    path: locations.home(),
    element: <AppPageLayout />,
    children: [
      {
        index: true,
        path: locations.dashboard(),
        element: <Dashboard />,
      },
      {
        path: locations.b2bThriftPlanList(),
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.b2bThriftPlanList(),
            element: <ThriftCompanies />,
          },
          {
            path: locations.b2bThriftPlan(),
            element: <CreateCompany />,
          },
          {
            path: locations.uploadCompanyDocument(),
            element: <UploadCompanyDocument />,
          },
          {
            path: locations.companyGroupList(),
            element: <Outlet />,
            children: [
              {
                index: true,
                path: locations.companyGroupList(),
                element: <CompanyGroups />,
              },
              {
                path: locations.companyGroup(undefined),
                element: <CreateCompanyGroup />,
              },
            ],
          },
          {
            path: locations.companyEmployeesList(),
            element: <Outlet />,
            children: [
              {
                index: true,
                path: locations.companyEmployeesList(),
                element: <OnboardingEmployees />,
              },
              {
                path: locations.companyEmployee(undefined, undefined),
                element: <CreateCompanyEmployee />,
              },
            ],
          },
          {
            path: locations.companyDepartmentsList(), // /companies/:companyId/departments
            element: <Outlet />,
            children: [
              {
                index: true,
                path: locations.companyDepartmentsList(),
                element: <CompanyDepartments />,
              },
              {
                path: locations.companyDepartment(undefined, undefined),
                element: <CreateCompanyDepartment />,
              },
            ],
          },
          {
            path: locations.companyWithdrawRequestList(),
            element: <Outlet />,
            children: [
              {
                index: true,
                path: locations.companyWithdrawRequestList(),
                element: <CompanyWithdrawRequests />,
              },
              {
                path: locations.companyWithdrawRequest(undefined),
                element: <CreateWithdrawRequest />,
              },
            ],
          },
          {
            path: locations.companyTreasury(),
            element: <CompanyTreasury />,
          },
          {
            path: locations.companyTreasuryOnboarding(),
            element: <CompanyTreasury showOnboarding />,
          },
        ],
      },
      {
        path: locations.ledgerB2c(),
        element: <LedgerB2c />,
      },
      {
        path: locations.ledgerB2b(),
        element: <LedgerB2b />,
      },
      {
        path: locations.ledger(),
        element: <Navigate to={locations.ledgerB2c()} replace />,
      },
      {
        path: locations.csvTemplates(),
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.csvTemplates(),
            element: <CsvTempletes />,
          },
          {
            path: locations.csvTemplate('create'),
            element: <CreateCsvTemplete />,
          },
          {
            path: locations.csvTemplate(undefined),
            element: <CreateCsvTemplete />,
          },
        ],
      },
      {
        path: locations.documentTypes(),
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.documentTypes(),
            element: <DocumentTypes />,
          },
          {
            path: locations.documentType('create'),
            element: <CreateDocumentType />,
          },
        ],
      },
      {
        path: locations.analyticsCron(),
        element: <AnalyticsCron />,
      },
      {
        path: locations.userList(),
        permission: permissions.User.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.userList(),
            permission: permissions.User.list,
            element: <UserList />,
          },
          {
            path: locations.user(),
            permission: permissions.User.read,
            element: <User />,
          },
        ],
      },
      {
        path: locations.earlySignupUserList(),
        permission: permissions.Anonymous.listEarlySignupUsers,
        element: <EarlySignupUserList />,
      },
      {
        path: locations.userGroupList(),
        permission: permissions.UserGroup.list,
        element: <UserGroupList />,
      },
      {
        path: locations.userGiftList(),
        permission: permissions.UserGift.list,
        element: <UserGiftList />,
      },
      {
        path: locations.employeeList(),
        permission: permissions.Employee.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.employeeList(),
            permission: permissions.Employee.list,
            element: <EmployeeList />,
          },
          {
            path: locations.employee(),
            permission: permissions.Employee.read,
            element: <Employee />,
          },
        ],
      },
      {
        path: locations.roleList(),
        permission: permissions.Role.list,
        element: <RoleList />,
      },
      {
        path: locations.currencyList(),
        permission: permissions.Currency.list,
        element: <CurrencyList />,
      },
      {
        path: locations.userCategoryList(),
        permission: permissions.Currency.list,
        element: <UserCategoryList />,
      },
      {
        path: locations.assetList(),
        permission: permissions.Asset.list,
        element: <AssetList />,
      },
      {
        path: locations.productTypeList(),
        permission: permissions.ProductType.list,
        element: <ProductTypeList />,
      },
      {
        path: locations.indexFundList(),
        permission: permissions.IndexFund.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.indexFundList(),
            permission: permissions.IndexFund.list,
            element: <IndexFundList />,
          },
          {
            path: locations.indexFund(),
            permission: permissions.IndexFund.read,
            element: <IndexFund />,
          },
          {
            path: locations.indexFundNavHistory(),
            permission: permissions.IndexFund.list,
            element: <IndexFundNavHistory />,
          },
        ],
      },
      {
        path: locations.indexFundNavList(),
        permission: permissions.IndexFund.list,
        element: <IndexFundNav />,
      },
      {
        path: locations.portfolioList(),
        permission: permissions.Portfolio.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.portfolioList(),
            permission: permissions.Portfolio.list,
            element: <PortfolioList />,
          },
          {
            path: locations.portfolio(),
            permission: permissions.Portfolio.read,
            element: <Portfolio />,
          },
        ],
      },
      {
        path: locations.benchmarkList(),
        permission: permissions.Benchmark.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.benchmarkList(),
            permission: permissions.Benchmark.list,
            element: <BenchmarkList />,
          },
          // {
          //   path: locations.benchmark(),
          //   permission: permissions.Benchmark.read,
          //   element: <Benchmark />,
          // },
        ],
      },
      {
        path: locations.portfolioOrderList(),
        permission: permissions.PortfolioOrder.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.portfolioOrderList(),
            permission: permissions.PortfolioOrder.list,
            element: <PortfolioOrderList />,
          },
          {
            path: locations.portfolioOrder(),
            permission: permissions.PortfolioOrder.read,
            element: <PortfolioOrder />,
          },
        ],
      },
      {
        path: locations.orderExecutionList(),
        permission: permissions.OrderExecution.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.orderExecutionList(),
            permission: permissions.OrderExecution.list,
            element: <OrderExecutionList />,
          },
          {
            path: locations.orderExecution(),
            permission: permissions.OrderExecution.read,
            element: <OrderExecution />,
          },
        ],
      },
      {
        path: locations.subscribedPortfolioList(),
        permission: permissions.SubscribedPortfolio.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.subscribedPortfolioList(),
            permission: permissions.SubscribedPortfolio.list,
            element: <SubscribedPortfolioList />,
          },
          {
            path: locations.subscribedPortfolio(),
            permission: permissions.SubscribedPortfolio.read,
            element: <SubscribedPortfolio />,
          },
        ],
      },
      {
        path: locations.userPortfolioRequestList(),
        permission: permissions.UserPortfolioRequest.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.userPortfolioRequestList(),
            permission: permissions.UserPortfolioRequest.list,
            element: <UserPortfolioRequestList />,
          },
          {
            path: locations.userPortfolioRequest(),
            permission: permissions.UserPortfolioRequest.list,
            element: <UserPortfolioRequest />,
          },
        ],
      },
      {
        path: locations.ibanVerification(),
        element: <IbanVerification />,
      },
      {
        path: locations.bankLookupList(),
        permission: permissions.BankLookup.list,
        element: <BankLookupList />,
      },
      {
        path: locations.alpacaFeeList(),
        permission: permissions.AlpacaFee.list,
        element: <AlpacaFeeList />,
      },
      {
        path: locations.alpacaFeeReport(),
        permission: permissions.AlpacaFee.report,
        element: <AlpacaFeeReport />,
      },
      {
        path: locations.settings(),
        permission: permissions.AppConfig.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.settings(),
            permission: permissions.AppConfig.list,
            element: <Settings />,
          },
          // {
          //   path: locations.subscribedPortfolio(),
          //   permission: permissions.UserPortfolioRequest.read,
          //   element: <UserPortfolioRequest />,
          // },
        ],
      },
      {
        path: locations.notificationTemplateList(),
        permission: permissions.NotificationTemplate.list,
        element: <NotificationTemplateList channel="PUSH" />,
      },
      {
        path: locations.emailTemplateList(),
        permission: permissions.NotificationTemplate.list,
        element: <NotificationTemplateList channel="EMAIL" />,
      },
      {
        path: locations.accountDeletionList(),
        permission: permissions.AccountDeletion.list,
        element: <AccountDeletionList />,
      },
      {
        path: locations.resourceList(),
        permission: permissions.Resource.list,
        element: <ResourceList />,
      },
      {
        path: locations.fundGeographicalFocus(),
        permission: permissions.FundGeographicalFocus.list,
        element: <FundGeographicalFocusList />,
      },
      {
        path: locations.fundManager(),
        permission: permissions.FundManager.list,
        element: <FundManagerList />,
      },
      {
        path: locations.holdingCompany(),
        permission: permissions.HoldingCompany.list,
        element: <HoldingCompanyList />,
      },
      {
        path: locations.fundAssetClass(),
        permission: permissions.FundAssetClass.list,
        element: <FundAssetClassList />,
      },
      {
        path: locations.fundValuationDays(),
        permission: permissions.FundValuationDay.list,
        element: <FundValuationDayList />,
      },
      {
        path: locations.fundValuationFrequency(),
        permission: permissions.FundValuationFrequency.list,
        element: <FundValuationFrequencyList />,
      },
      {
        path: locations.actionList(),
        permission: permissions.Action.list,
        element: <ActionList />,
      },
      {
        path: locations.rolePermissionList(),
        permission: permissions.RolePermission.list,
        element: <RolePermissionList />,
      },
      {
        path: locations.rolePermissionList2(),
        permission: permissions.RolePermission.list,
        element: <Permissions />,
      },
      {
        path: locations.payment(),
        permission: permissions.PaymentPermission.paymentStatement,
        element: <Payment />,
      },
      {
        path: locations.feeConfigList(),
        // permission: permissions.Portfolio.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.feeConfigList(),
            // permission: permissions.Portfolio.list,
            element: <FeeConfigList />,
          },
          {
            path: locations.feeConfig(),
            // permission: permissions.Portfolio.read,
            element: <FeeConfig />,
          },
        ],
      },
      {
        path: locations.report(),
        element: <Outlet />,
        children: [
          {
            index: true,
            permission: permissions.ReportPermission.list,
            element: <Navigate to={locations.reportHub()} replace />,
          },
          // Finance reports: open to every logged-in admin, so no report permission here. A static path wins over :reportSegment.
          {
            path: REPORT_SEGMENT_USER_DETAILS,
            element: (
              <Container component="main" maxWidth="xl">
                <UserDetailsReportPage />
              </Container>
            ),
          },
          {
            path: REPORT_SEGMENT_FEE_CALCULATIONS,
            element: (
              <Container component="main" maxWidth="xl">
                <FeeCalculationsReportPage />
              </Container>
            ),
          },
          {
            path: REPORT_SEGMENT_FINANCE_USERS,
            element: (
              <Container component="main" maxWidth="xl">
                <FinanceUsersReportPage />
              </Container>
            ),
          },
          {
            path: REPORT_SEGMENT_PORTFOLIO_COMPOSITION,
            element: (
              <Container component="main" maxWidth="xl">
                <PortfolioCompositionReportPage />
              </Container>
            ),
          },
          {
            path: REPORT_SEGMENT_PORTFOLIO_SNAPSHOT,
            element: (
              <Container component="main" maxWidth="xl">
                <PortfolioSnapshotReportPage />
              </Container>
            ),
          },
          {
            path: ':reportSegment',
            permission: permissions.ReportPermission.list,
            element: <Report />,
          },
        ],
      },
      {
        path: locations.promoCodes(),
        // permission: permissions.promoCodes.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.promoCodes(),
            element: <PromoCodesList />,
          },
          {
            path: locations.promoCodeUsages(),
            element: <PromoCodeUsages />,
          },
        ],
      },
      {
        path: locations.analytics(),
        // TODO: add analytics to admin-serivce to use permission functionality
        // permission: permissions.AnalyticsPermission.list,
        element: <Analytics />,
      },
      {
        path: locations.userFeeDeductionList(),
        // permission: permissions.UserFeeDeduction.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.userFeeDeductionList(),
            // permission: permissions.UserFeeDeduction.list,
            element: <UserFeeDeductionList />,
          },
          {
            path: locations.userFeeDeduction(),
            // permission: permissions.UserFeeDeduction.list,
            element: <UserFeeDeduction />,
          },
        ],
      },
      {
        path: locations.feeBatchList(),
        // permission: permissions.FeeBatch.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.feeBatchList(),
            // permission: permissions.FeeBatch.list,
            element: <FeeBatchList />,
          },
          {
            path: locations.feeBatch(),
            // permission: permissions.FeeBatch.read,
            element: <FeeBatch />,
          },
        ],
      },
      {
        path: locations.batchExecutionList(),
        // permission: permissions.BatchExecution.list,
        element: <Outlet />,
        children: [
          {
            index: true,
            path: locations.batchExecutionList(),
            // permission: permissions.BatchExecution.list,
            element: <BatchExecutionList />,
          },
          {
            path: locations.batchExecution(),
            // permission: permissions.BatchExecution.read,
            element: <BatchExecution />,
          },
        ],
      },
      {
        path: '*',
        element: <Navigate to={locations.notFound()} />,
      },
    ],
  },
  {
    path: locations.home(),
    element: <PublicPageLayout />,
    children: [
      {
        path: locations.login(),
        element: <Login />,
      },
      {
        path: locations.forgotPassword(),
        element: <ForgotPassword />,
      },
      {
        path: locations.notFound(),
        element: <Error />,
      },
      {
        path: '*',
        element: <Navigate to={locations.notFound()} />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={locations.home()} replace />,
  },
];

export function AppRoutes() {
  const { isLoggedIn } = useSelector(authSelector);
  const { data: myPermissions, isSuccess } = useFetchMyPermissionsQuery(undefined, {
    pollingInterval: 5 * 60000, // every 5 min
    skip: !isLoggedIn,
  });
  const [routes, setRoutes] = useState<RouteObject[]>(routesByPermissions as RouteObject[]);
  const filterRoutesByPermissions = (initialRoutes: PermissionRouteObject[]) =>
    initialRoutes.reduce((res, { permission, ...item }) => {
      if (!permission || (myPermissions || {})[permission.resource]?.includes(permission.action)) {
        if (item.children) {
          res.push({
            ...item,
            children: filterRoutesByPermissions(item.children),
          } as RouteObject);
        } else {
          res.push(item as RouteObject);
        }
      }

      return res;
    }, [] as RouteObject[]);

  useEffect(() => {
    if (isSuccess) {
      const newRoutes = filterRoutesByPermissions(routesByPermissions);

      setRoutes(newRoutes);
    }
  }, [myPermissions, isSuccess]);

  return useRoutes(routes);
}
